import type { FC } from "react"

import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react"
import { ChevronUpDownIcon } from "@heroicons/react/16/solid"
import { CheckIcon } from "@heroicons/react/20/solid"

export type TailwindSelectorProps = {
  options: { id: string; label: string }[]
  label?: string
  selected?: { id: string; label: string } | null
  onSelectChange: (value: string) => void
  labelClassName?: string
}
const TailwindSelector: FC<TailwindSelectorProps> = ({
  options,
  label,
  selected,
  onSelectChange,
  labelClassName = `text-gray-900`,
}) => {
  // Create a placeholder empty selection
  const emptySelection = { id: ``, label: `Select an option` }

  // Use provided selection or default to empty
  const currentSelection = selected ?? emptySelection

  // Handle selection change and extract the id to pass to parent
  const handleSelectionChange = (option: { id: string; label: string }) => {
    onSelectChange(option.id)
  }
  return (
    <Listbox value={selected ?? undefined} onChange={handleSelectionChange}>
      {label ? (
        <Label className={`block text-sm/6 font-medium ${labelClassName}`}>
          {label}
        </Label>
      ) : null}
      <div className="relative mt-2">
        <ListboxButton className="grid w-full cursor-default grid-cols-1 rounded-md bg-white py-2 pr-2 pl-3 text-left text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-600 sm:text-sm/6">
          <span className="col-start-1 row-start-1 truncate pr-6">
            {currentSelection.label}
          </span>
          <ChevronUpDownIcon
            aria-hidden="true"
            className="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4"
          />
        </ListboxButton>

        <ListboxOptions
          transition
          className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg outline-1 outline-black/5 data-leave:transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 sm:text-sm"
        >
          {options.map((opt) => (
            <ListboxOption
              key={opt.id}
              value={opt}
              className="group relative cursor-default py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-blue-600 data-focus:text-white data-focus:outline-hidden"
            >
              <span className="block truncate font-normal group-data-selected:font-semibold">
                {opt.label}
              </span>

              <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600 group-data-focus:text-white group-[:not([data-selected])]:hidden">
                <CheckIcon aria-hidden="true" className="size-5" />
              </span>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  )
}

export default TailwindSelector
